<template>
  <div>
    <v-dialog v-model="modalStatus" max-width="580" scrollable persistent>
      <v-card class="modal-card">

        <!-- Header -->
        <div class="modal-header">
          <div class="header-left">
            <div class="header-icon">
              <v-icon size="15" color="white">mdi-pencil</v-icon>
            </div>
            <span class="header-title">Editar Partida</span>
          </div>
          <button class="close-btn" @click="closeModal">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <v-card-text class="modal-body pa-0">
          <div class="form-content">

            <!-- Etapa + Título -->
            <div class="fields-row">
              <div class="field-group" style="width: 160px; flex-shrink: 0;">
                <label class="field-label">Etapa</label>
                <input
                  v-model="protocolo.etapa"
                  type="text"
                  class="field-input"
                  placeholder="Ej: Estructura"
                />
              </div>
              <div class="field-group" style="flex: 1;">
                <label class="field-label">Nombre de la Partida</label>
                <input
                  v-model="protocolo.title"
                  type="text"
                  class="field-input"
                  placeholder="Ingrese nombre de la partida"
                />
              </div>
            </div>

            <!-- Subpartidas -->
            <div class="section-header">
              <span class="section-title">SubPartidas</span>
              <span class="section-count" v-if="grupos.length > 0">{{ grupos.length }}</span>
              <button class="btn-add-grupo" @click="addGrupo">
                <v-icon size="13">mdi-plus</v-icon>
                Agregar SubPartida
              </button>
            </div>

            <!-- Empty grupos state -->
            <div v-if="grupos.length === 0" class="empty-grupos">
              <v-icon size="32" color="#d1d5db">mdi-group</v-icon>
              <p>No hay subpartidas. Agrega una para comenzar.</p>
            </div>

            <!-- Grupos list -->
            <div
              v-for="(grupo, index) in grupos"
              :key="index"
              class="grupo-block"
            >
              <!-- Grupo header -->
              <div class="grupo-header">
                <div class="grupo-input-wrapper">
                  <v-icon size="14" color="#5e85ef" class="mr-1">mdi-folder-outline</v-icon>
                  <input
                    v-model="grupo.name"
                    type="text"
                    class="grupo-input"
                    placeholder="Nombre del grupo"
                  />
                </div>
                <div class="grupo-actions">
                  <button class="btn-add-task" @click="addTask(grupo.name)" :disabled="!grupo.name">
                    <v-icon size="13">mdi-plus</v-icon>
                    Tarea
                  </button>
                  <button class="btn-remove-grupo" @click="removeGrupo(index)">
                    <v-icon size="14">mdi-delete-outline</v-icon>
                  </button>
                </div>
              </div>

              <!-- Tasks -->
              <div class="tasks-list">
                <div
                  v-for="(task, taskIndex) in protocolo.tasks.filter(i => i.grupo === grupo.name)"
                  :key="taskIndex"
                  class="task-row"
                >
                  <div class="task-dot"></div>
                  <input
                    v-model="task.title"
                    type="text"
                    class="task-input"
                    placeholder="Nombre de la tarea"
                  />
                  <button class="btn-remove-task" @click="removeTask(taskIndex)">
                    <v-icon size="13">mdi-close</v-icon>
                  </button>
                </div>

                <div v-if="protocolo.tasks.filter(i => i.grupo === grupo.name).length === 0" class="no-tasks">
                  Sin tareas aún
                </div>
              </div>
            </div>

          </div>
        </v-card-text>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">Cancelar</button>
          <button
            class="btn-save"
            :class="{ loading: loading }"
            :disabled="loading"
            @click="updateProtocolo"
          >
            <v-progress-circular v-if="loading" indeterminate color="white" size="14" width="2" class="mr-2"></v-progress-circular>
            <v-icon v-else size="14" class="mr-1">mdi-check</v-icon>
            {{ loading ? 'Guardando...' : 'Guardar Cambios' }}
          </button>
        </div>

      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { updateProtocolo } from '@/helpers/api/entrega';

export default {
  name: 'EditProtocoloModal',
  props: {
    closeModal: { type: Function, required: true },
    modalStatus: { type: Boolean, default: false },
    protocoloToEdit: { type: Object, default: null }
  },
  data() {
    return {
      protocolo: { id: null, title: null, etapa: null, tasks: [] },
      grupos: [],
      loading: false
    };
  },
  watch: {
    protocoloToEdit(newVal) {
      if (newVal) {
        this.protocolo = JSON.parse(JSON.stringify(newVal));
        const gruposSet = new Set();
        if (this.protocolo.tasks?.length > 0) {
          this.protocolo.tasks.forEach(task => { if (task.grupo) gruposSet.add(task.grupo); });
          this.grupos = Array.from(gruposSet).map(name => ({ name }));
        } else {
          this.protocolo.tasks = [];
          this.grupos = [];
        }
      }
    }
  },
  methods: {
    addTask(grupo) {
      if (!grupo) return;
      this.protocolo.tasks.push({ title: null, descripcion: null, grupo });
    },
    removeTask(index) {
      this.protocolo.tasks.splice(index, 1);
    },
    addGrupo() {
      this.grupos.push({ name: null });
    },
    removeGrupo(index) {
      const grupoName = this.grupos[index].name;
      this.grupos.splice(index, 1);
      if (grupoName) {
        this.protocolo.tasks = this.protocolo.tasks.filter(t => t.grupo !== grupoName);
      }
    },
    showError(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message, title: 'Error', created: new Date(),
        type: 'error', icon: 'mdi-alert-circle', color: '#fb8c00'
      });
    },
    async updateProtocolo() {
      if (!this.protocolo.title) return this.showError('Por favor escribe un título.');
      if (!this.protocolo.tasks?.length) return this.showError('Por favor incluye al menos una tarea.');
      if (this.protocolo.tasks.some(t => !t.title)) return this.showError('Todas las tareas deben tener un título.');

      this.loading = true;
      try {
        this.protocolo.project_id = this.$store.projectsStore.state.selectedProject.id;
        const response = await updateProtocolo(this.protocolo);
        if (response?.code === 200) {
          this.$store.globalStore.dispatch('addMessage', {
            message: 'Protocolo actualizado exitosamente', title: 'Éxito',
            created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
          });
          this.closeModal();
        } else throw new Error();
      } catch {
        this.showError('Error al actualizar el protocolo');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.header-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: linear-gradient(135deg, #5e85ef, #3d6de0);
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
}
.close-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: background 0.15s, color 0.15s;
}
.close-btn:hover { background: #f1f5f9; color: #475569; }

/* Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.modal-body::-webkit-scrollbar { width: 4px; }
.modal-body::-webkit-scrollbar-track { background: transparent; }
.modal-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

.form-content {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Fields */
.fields-row {
  display: flex;
  gap: 12px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.field-input {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  width: 100%;
}
.field-input:focus {
  border-color: #5e85ef;
  background: #fff;
}
.field-input::placeholder { color: #b0bac8; }

/* Section header */
.section-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}
.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.section-count {
  font-size: 11px;
  font-weight: 700;
  color: #5e85ef;
  background: #eff4ff;
  padding: 1px 7px;
  border-radius: 10px;
}
.btn-add-grupo {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-add-grupo:hover {
  background: #5e85ef;
  border-color: #5e85ef;
  color: #fff;
}

/* Empty grupos */
.empty-grupos {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 0;
  color: #94a3b8;
}
.empty-grupos p {
  margin: 0;
  font-size: 13px;
}

/* Grupo block */
.grupo-block {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
}

.grupo-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.grupo-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 8px;
  height: 30px;
  transition: border-color 0.15s;
}
.grupo-input-wrapper:focus-within { border-color: #5e85ef; }

.grupo-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
}
.grupo-input::placeholder { color: #b0bac8; font-weight: 400; }

.grupo-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.btn-add-task {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-add-task:hover:not(:disabled) {
  background: #5e85ef;
  border-color: #5e85ef;
  color: #fff;
}
.btn-add-task:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-remove-grupo {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #fff5f5;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.btn-remove-grupo:hover { background: #ef4444; border-color: #ef4444; color: #fff; }

/* Tasks */
.tasks-list {
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.1s;
}
.task-row:hover { background: #f8fafc; }

.task-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #cbd5e1;
  flex-shrink: 0;
}

.task-input {
  flex: 1;
  border: none;
  border-bottom: 1px solid transparent;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #334155;
  padding: 2px 0;
  transition: border-color 0.15s;
}
.task-input:focus { border-bottom-color: #5e85ef; }
.task-input::placeholder { color: #b0bac8; }

.btn-remove-task {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  opacity: 0;
}
.task-row:hover .btn-remove-task { opacity: 1; }
.btn-remove-task:hover { background: #fee2e2; color: #ef4444; }

.no-tasks {
  font-size: 12px;
  color: #cbd5e1;
  padding: 6px 6px 4px;
  font-style: italic;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0 16px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-cancel:hover { background: #f8fafc; border-color: #cbd5e1; color: #475569; }

.btn-save {
  display: inline-flex;
  align-items: center;
  padding: 0 16px;
  height: 34px;
  border-radius: 7px;
  border: none;
  background: linear-gradient(135deg, #2D4B95, #1e3a7a);
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 1px 4px rgba(45, 75, 149, 0.35);
}
.btn-save:hover:not(.loading) {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(45, 75, 149, 0.4);
}
.btn-save.loading { opacity: 0.75; cursor: not-allowed; }
</style>